import Drawer from '@mui/material/Drawer';
import React, { Component, type JSX, type ReactNode } from 'react';
import type { StoredDownloadJob } from '../@types/repository-groups';
import type {
  AuditLogExportFormat,
  DataTableFormat,
  RepositoryFragment
} from '../__generated__/apollo-hooks';
import { DownloadManager } from '../components/download-manager/download-manager.component';
import { CenteredBox } from '../components/styled-components/layouts/layouts.styled';
import { DOHERTY_THRESHOLD } from '../constants/general';
import {
  canStartDownload,
  DOWNLOAD_POLL_TIMEOUT_MS,
  dedupeDownloads,
  downloadSourceIdentity,
  isDownloadInProgress,
  migrateStoredManifest,
  rehydrateDownloadState
} from '../helpers/download-manifest.helper';
import { db } from '../helpers/indexedDb.helper';
import { requestNotificationPermission } from '../helpers/notification.helper';
import { getTabId, type UUID, uuid } from '../helpers/uuid.helper';
import { UserContext, type UserContextState } from './user.context';

type DownloadsContextProps = {
  children: ReactNode[] | ReactNode;
};

export type DownloadState = 'PENDING' | 'SUCCESS' | 'FAILURE' | 'DOWNLOADING';

export const REMOVE_DOWNLOAD_DELAY = DOHERTY_THRESHOLD * 6;

export enum ManifestKind {
  AUTHENTICATED = 1,
  AUTHORIZED = 2
}

export type DownloadManifestIndexDb = {
  indexDbId?: UUID;
  state?: DownloadState;
  startTimestamp?: number;
  /** Persisted so a failure restored after a reload still says why. */
  errorMessage?: string;
};

/**
 * Everything needed to reach a download's URL, in a form that survives
 * `JSON.stringify` — which is what lets a restored job resume.
 */
export type DownloadSource =
  | {
      /** The URL is already known — nothing to start, nothing to poll. */
      type: 'STATIC';
      sourcePath: string;
    }
  | {
      type: 'DATA_TABLE';
      organizationId: string;
      recipeRunId: string;
      dataTable: string;
      format: DataTableFormat;
      group?: string;
      /** Server job id; never set when the mutation answers with a finished URL. */
      downloadId?: string;
      processingStartTimestamp?: number;
    }
  | {
      type: 'AUDIT_LOG';
      format: AuditLogExportFormat;
      /** ISO strings — a `Date` does not round-trip through `JSON.parse`. */
      since?: string;
      until?: string;
      downloadId?: string;
    };

export type DataTableDownloadSource = Extract<
  DownloadSource,
  { type: 'DATA_TABLE' }
>;
export type AuditLogDownloadSource = Extract<
  DownloadSource,
  { type: 'AUDIT_LOG' }
>;

type GenericDownloadManifest = {
  id: string;
  fileName: string;
  /** Optional only for legacy rows; see {@link NewDownloadManifest}. */
  source?: DownloadSource;
  /** Error shown on a 204 (no content) response. */
  emptyMessage?: string;
};

export type AuthenticatedDownloadManifest = GenericDownloadManifest & {
  kind: ManifestKind.AUTHENTICATED;
};

export type AuthorizedDownloadManifest = GenericDownloadManifest & {
  kind: ManifestKind.AUTHORIZED;
  repository: RepositoryFragment;
};

export type DownloadManifest = (
  | AuthenticatedDownloadManifest
  | AuthorizedDownloadManifest
) &
  DownloadManifestIndexDb & {
    tabId?: UUID;
  };

/**
 * What `manageDownload` accepts. `source` is required here so a new download
 * can't omit it, while {@link DownloadManifest} stays honest about legacy rows.
 */
type NewDownloadManifest = DownloadManifest & {
  source: DownloadSource;
};

type DownloadsContextState = {
  showDownloads: boolean;
  minimized: boolean;
  downloads: DownloadManifest[];
  manageDownload: (download: NewDownloadManifest) => void;
  updateDownloadState: (
    downloadJobId: UUID,
    state: DownloadState,
    errorMessage?: string
  ) => void;
  updateDownloadSource: (downloadJobId: UUID, source: DownloadSource) => void;
  removeDownload: (downloadJobId: UUID) => void;
  toggleMinimize: () => void;
  resetState: () => void;
};

const initialState: DownloadsContextState = {
  showDownloads: false,
  minimized: false,
  downloads: [],
  // biome-ignore lint/suspicious/noEmptyBlockStatements: initial state
  manageDownload: () => {},
  // biome-ignore lint/suspicious/noEmptyBlockStatements: initial state
  updateDownloadState: () => {},
  // biome-ignore lint/suspicious/noEmptyBlockStatements: initial state
  updateDownloadSource: () => {},
  // biome-ignore lint/suspicious/noEmptyBlockStatements: initial state
  removeDownload: () => {},
  // biome-ignore lint/suspicious/noEmptyBlockStatements: initial state
  toggleMinimize: () => {},
  // biome-ignore lint/suspicious/noEmptyBlockStatements: initial state
  resetState: () => {}
};

export const DownloadsContext = React.createContext(initialState);
export class DownloadsProvider extends Component<
  DownloadsContextProps,
  DownloadsContextState
> {
  static contextType?: React.Context<UserContextState> = UserContext;
  context: UserContextState;

  readonly state: DownloadsContextState = {
    ...initialState,
    manageDownload: async (download: NewDownloadManifest) => {
      await requestNotificationPermission();
      const downloadsFromIndexedDb = await this.loadAndPruneDownloadJobs();
      download.indexDbId = uuid();
      download.startTimestamp = Date.now();
      download.state = 'PENDING';
      download.tabId = getTabId();

      this.setState((prevState) => {
        const existing = dedupeDownloads([
          ...prevState.downloads,
          ...downloadsFromIndexedDb
        ]);
        /**
         * Only a row still running this same request blocks it. Keyed on source
         * rather than file name, so a restored job for one recipe run can't
         * swallow a click for another run of the same table.
         */
        const identity = downloadSourceIdentity(download.source);
        const isSameRequest = (d: DownloadManifest) =>
          canStartDownload(d) && downloadSourceIdentity(d.source) === identity;
        /** Nothing else clears a legacy row before retention. */
        const isStaleGhost = (d: DownloadManifest) =>
          !canStartDownload(d) && d.fileName === download.fileName;
        const alreadyDownloading = existing.some(
          (d) => isSameRequest(d) && isDownloadInProgress(d.state)
        );
        const downloads = alreadyDownloading
          ? existing
          : [
              ...existing.filter((d) => !isSameRequest(d) && !isStaleGhost(d)),
              download
            ];

        return {
          showDownloads: true,
          downloads,
          minimized: false
        };
      });
    },
    updateDownloadState: async (
      downloadJobId: UUID,
      state: DownloadState,
      errorMessage?: string
    ) => {
      this.setState((prevState) => {
        const downloads = [...prevState.downloads];
        const download = downloads.find((d) => d.indexDbId === downloadJobId);

        if (!download || download.state === state) {
          return;
        }

        download.state = state;
        download.errorMessage = errorMessage;

        return {
          downloads
        };
      });
    },
    /**
     * Re-persists the source once the resolver learns the server's job id;
     * `componentDidUpdate` writes it through, so a reload can resume.
     */
    updateDownloadSource: (downloadJobId: UUID, source: DownloadSource) => {
      this.setState((prevState) => {
        const downloads = [...prevState.downloads];
        const download = downloads.find((d) => d.indexDbId === downloadJobId);

        if (!download) {
          return;
        }

        download.source = source;

        return {
          downloads
        };
      });
    },
    removeDownload: (downloadJobId: UUID) => {
      this.setState((prevState) => {
        const downloads = prevState.downloads.filter(
          (d) => d.indexDbId !== downloadJobId
        );
        const showDownloads = downloads.length > 0;

        if (!showDownloads) {
          this.context.setPreferences({
            minimizeDownloadManager: initialState.minimized
          });
        }

        return {
          downloads,
          showDownloads
        };
      });
    },
    resetState: () => {
      this.setState(() => ({
        downloads: initialState.downloads,
        showDownloads: initialState.showDownloads,
        minimized: initialState.minimized
      }));
      this.context.setPreferences({
        minimizeDownloadManager: initialState.minimized
      });
    },
    toggleMinimize: () => {
      this.setState((prevState) => {
        const minimized = !prevState.minimized;
        this.context.setPreferences({ minimizeDownloadManager: minimized });

        return {
          minimized
        };
      });
    }
  };

  private visibilityHandler = async () => {
    if (document.visibilityState === 'visible') {
      const downloadsFromIndexedDb = await this.loadAndPruneDownloadJobs();
      const { minimizeDownloadManager } =
        this.context.getDefaultsFromLocalStorage();

      this.setState((prevState) => {
        const downloads = dedupeDownloads([
          ...prevState.downloads,
          ...downloadsFromIndexedDb
        ]);

        return {
          downloads,
          minimized: minimizeDownloadManager,
          showDownloads: downloads.length > 0
        };
      });
    }
  };

  async componentDidMount(): Promise<void> {
    const downloadsFromIndexedDb = await this.loadAndPruneDownloadJobs();

    this.setState((prevState) => {
      const downloads = dedupeDownloads([
        ...prevState.downloads,
        ...downloadsFromIndexedDb
      ]);

      return {
        minimized: this.context.preferences.minimizeDownloadManager,
        downloads,
        showDownloads: downloads.length > 0
      };
    });

    document.addEventListener(
      'visibilitychange',
      this.visibilityHandler,
      false
    );
  }

  async componentDidUpdate(
    _prevProps,
    prevState: DownloadsContextState
  ): Promise<void> {
    if (prevState.downloads !== this.state.downloads) {
      await this.updateIndexedDB();
    }
  }

  componentWillUnmount() {
    document.removeEventListener(
      'visibilitychange',
      this.visibilityHandler,
      false
    );
  }

  // #region IndexedDB
  private async updateIndexedDB(): Promise<void> {
    // Clear existing download jobs in IndexedDB with this same tabId
    const tabJobs = await this.loadAndPruneDownloadJobs();
    if (tabJobs && tabJobs.length > 0) {
      await db.downloadJobs.bulkDelete(tabJobs.map((job) => job.indexDbId));
    }

    await db.downloadJobs.bulkPut(
      this.state.downloads.map(this.mapDownloadManifestToDownloadJob)
    );
  }

  /**
   * Returns this tab's unexpired jobs and deletes expired rows. `getTabId()` is
   * sessionStorage-backed, so a reload reclaims its own; another tab's are left
   * alone rather than adopted, though a `source` would make them resumable.
   */
  private async loadAndPruneDownloadJobs(): Promise<DownloadManifest[]> {
    const jobs: StoredDownloadJob[] = await db.downloadJobs.toArray();
    // Any shorter, and a reload or navigation drops a job still polling.
    const expiredBefore = Date.now() - DOWNLOAD_POLL_TIMEOUT_MS;
    const deletableJobIds: UUID[] = [];
    const tabJobs: DownloadManifest[] = [];

    for (const job of jobs) {
      let manifest: DownloadManifest;

      try {
        manifest = migrateStoredManifest(JSON.parse(job.manifest));
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: we want to log this error for debugging purposes
        console.error(
          'Failed to parse download job manifest:',
          job.manifest,
          error
        );
        deletableJobIds.push(job.id);
        continue;
      }

      if (!manifest.startTimestamp || manifest.startTimestamp < expiredBefore) {
        deletableJobIds.push(job.id);
      } else if (manifest.tabId === getTabId()) {
        tabJobs.push({ ...manifest, state: rehydrateDownloadState(manifest) });
      }
    }

    if (deletableJobIds.length > 0) {
      await db.downloadJobs.bulkDelete(deletableJobIds);
    }

    return tabJobs;
  }

  private mapDownloadManifestToDownloadJob = (
    downloadManifest: DownloadManifest
  ): StoredDownloadJob => ({
    id: downloadManifest.indexDbId,
    manifest: JSON.stringify(downloadManifest)
  });
  // #endregion

  /** The manager renders inside the provider so it can read this context with hooks. */
  render(): JSX.Element {
    return (
      <DownloadsContext.Provider value={this.state}>
        {this.props.children}
        <Drawer
          anchor="bottom"
          /**
           * Important for preserving the drawer while also allowing for
           * interaction with the page while downloads are downloading
           */
          variant="persistent"
          sx={{
            ['& .MuiDrawer-paper']: {
              background: 'transparent',
              border: 'none',
              paddingTop: (theme) => theme.spacing(3),
              pointerEvents: 'none'
            }
          }}
          open={this.state.showDownloads}
        >
          <CenteredBox>
            <DownloadManager downloadManifests={this.state.downloads} />
          </CenteredBox>
        </Drawer>
      </DownloadsContext.Provider>
    );
  }
}
